import { CheckCircle2, Flag } from "lucide-react";
import { concernCount } from "./item-status";
import type { ReportItem, ItemSummary } from "../types";

const STRIP_COUNTS = [
  { key: "accepted", label: "Accepted", dot: "bg-green-500" },
  { key: "pending", label: "Pending", dot: "bg-amber-500" },
  { key: "inReview", label: "In review", dot: "bg-blue-500" },
  { key: "concern", label: "Concern", dot: "bg-red-500" },
] as const;

/** Where the run stands: decided of total, with a colored count per status.
 * Dots are decoration; every count carries its label. */
export function ItemSummaryStrip({
  summary,
  items,
}: {
  summary: ItemSummary;
  items: ReportItem[];
}) {
  const inProgress = items.filter((item) => item.decision === null).length;
  const counts: { key: string; label: string; dot: string; count: number }[] =
    STRIP_COUNTS.map((entry) => ({ ...entry, count: summary[entry.key] }));
  counts.push({
    key: "in-progress",
    label: "In progress",
    dot: "bg-slate-400",
    count: inProgress,
  });
  return (
    <div
      role="status"
      aria-label="Item review summary"
      className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-3 text-xs"
    >
      {summary.complete && summary.concern === 0 && (
        <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-800">
          <CheckCircle2 className="size-4" aria-hidden />
          Verification complete
        </span>
      )}
      {summary.complete && summary.concern > 0 && (
        <span className="inline-flex items-center gap-1.5 font-semibold text-red-800">
          <Flag className="size-4" aria-hidden />
          Review complete · {concernCount(summary.concern)}
        </span>
      )}
      <span className="text-muted-foreground">
        {summary.accepted + summary.concern} of {summary.total} items reviewed
      </span>
      {summary.carried > 0 && (
        <span className="text-muted-foreground">
          {summary.carried} carried forward
        </span>
      )}
      {counts
        .filter((entry) => entry.count > 0)
        .map((entry) => (
          <span key={entry.key} className="inline-flex items-center gap-1.5">
            <span className={`size-2 rounded-full ${entry.dot}`} aria-hidden />
            {entry.count} {entry.label}
          </span>
        ))}
    </div>
  );
}
